import React, { useState, useEffect, useRef, useCallback } from 'react'; // ✅ Imported useCallback
import axios from 'axios';
import './CandidateResumes.css';

const CandidateResumes = () => {
  const [resumePath, setResumePath] = useState(null); 
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null); 
  const userId = localStorage.getItem('userId');

  // ✅ Wrapped in useCallback to allow dependency injection in useEffect
  const fetchResumeData = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/profile/${userId}`);
      if (response.data && response.data.resumeFile) {
        setResumePath(response.data.resumeFile);
      } else {
        setResumePath(null); 
      }
    } catch (error) {
      console.error('Failed to fetch resume data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]); // ✅ Dependency: userId

  useEffect(() => {
    fetchResumeData();
  }, [fetchResumeData]); // ✅ Dependency added

  const handleUpdateButtonClick = () => {
    fileInputRef.current.click(); 
  };

  const handleFileUpdate = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }
    if (file.type !== 'application/pdf') {
      alert('Only PDF files (.pdf) are allowed.');
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }

    const formData = new FormData();
    formData.append('resumeFile', file);

    setIsUploading(true);
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/resume/${userId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResumePath(response.data.resumeFile); 
      alert('Resume updated successfully!');
    } catch (error) {
      console.error('File update failed:', error.response?.data || error.message);
      alert('File update failed: ' + (error.response?.data?.message || 'Server error'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  const handleDeleteResume = async () => {
    if (!window.confirm("Are you sure you want to delete your resume? You won't be able to apply for jobs.")) {
      return;
    }
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/resume/${userId}`);
      setResumePath(null); 
      alert('Resume deleted successfully.');
    } catch (error) {
      console.error('Failed to delete resume:', error.response?.data || error.message);
      alert('Could not delete resume: ' + (error.response?.data?.message || 'Server error'));
    }
  };

  // Split by '/' to get the filename, then split by '?' to remove Signed URL params
  const fileName = resumePath 
    ? resumePath.split('/').pop().split('?')[0] 
    : 'No resume uploaded';

  if (isLoading) {
    return (
      <div className="resumes-container">
        <h2 className="content-title">Resumes</h2>
        <p>Loading resume details...</p>
      </div>
    );
  }

  return (
    <div className="resumes-container">
      <div className="resumes-header">
        <h2 className="content-title">Resumes</h2>
        <button 
          className="add-resume-button" 
          onClick={handleUpdateButtonClick} 
          disabled={isUploading}
        >
          {isUploading ? 'Uploading...' : (resumePath ? 'Update Resume' : 'Upload Resume')}
        </button>
        <input 
          type="file" 
          ref={fileInputRef} 
          onChange={handleFileUpdate} 
          style={{ display: 'none' }} 
          name="resumeFile" 
          accept=".pdf"
        />
      </div>
      
      <div className="resumes-table-wrapper">
        <table className="resumes-table">
          <thead>
            <tr>
              <th>File Name</th>
              <th>Status</th>
              <th>Uploaded Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!resumePath ? (
              <tr>
                <td colSpan="4" className="empty-state">No resume uploaded yet. Click "Upload Resume".</td>
              </tr>
            ) : (
              <tr className="primary-row">
                <td>
                  {fileName}
                  <span className="primary-badge">Primary</span>
                </td>
                <td>Active</td>
                <td>{/* Date Logic */}</td>
                <td>
                  <a 
                    href={resumePath} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="action-link view"
                  >
                    View/Download
                  </a>
                  <button 
                    onClick={handleUpdateButtonClick} 
                    className="action-link set-primary"
                    disabled={isUploading}
                  >
                    {isUploading ? 'Uploading...' : 'Update'}
                  </button>
                  <button 
                    onClick={handleDeleteResume} 
                    className="action-link delete"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CandidateResumes;